import {Box, LogoSuite, Section, Text, Tiles} from '@primer/react-brand'
import type {ReactElement, ReactNode} from 'react'
import {Logo} from '../../../components/FlexSuiteLogos/Logo'
import type {IntroLogoType} from '../../../types/templates/templateFlexSuiteDetails'
import type {IntroSectionProps, LogobarOptions, LogoSuiteOptions} from './types'
import {IntroIconCards, IntroImageCards, IntroPillars, IntroSimpleIntro, IntroStats} from './variants'
import styles from './IntroSection.module.css'

export function IntroSection({
  introLogoHeading,
  introLogoType,
  introLogos,
  introLogoLink,
  introContext,
  introContextHeading,
  introContextLink,
  introContextBody,
  introItems,
  introIconCards,
  introPillars,
  introImageCards,
  introStats,
  logobarOptions,
  logoSuiteOptions,
  hideLogoHeading = true,
  hasMarqueeTopBorder = false,
}: IntroSectionProps) {
  const introContextContent = renderIntroContextContent({
    introContext,
    introContextHeading,
    introContextLink,
    introContextBody,
    introItems,
    introIconCards,
    introPillars,
    introImageCards,
    introStats,
  })

  const hasIntroContent = Boolean(introLogos?.length) || introContextContent !== null

  if (!hasIntroContent) return null

  return (
    <Section fullWidth paddingBlockEnd="none" paddingBlockStart="none">
      {introLogos && introLogos.length > 0
        ? renderIntroLogos({
            introLogos,
            introLogoType,
            introLogoHeading,
            introLogoLink,
            logobarOptions,
            logoSuiteOptions,
            hideLogoHeading,
            hasMarqueeTopBorder,
          })
        : null}
      {introContextContent}
    </Section>
  )
}

type RenderIntroLogosArgs = {
  introLogos: string[]
  introLogoType: IntroLogoType | undefined
  introLogoHeading: IntroSectionProps['introLogoHeading']
  introLogoLink: IntroSectionProps['introLogoLink']
  logobarOptions: LogobarOptions | undefined
  logoSuiteOptions: LogoSuiteOptions | undefined
  hideLogoHeading: boolean
  hasMarqueeTopBorder: boolean
}

function renderIntroLogos({
  introLogos,
  introLogoType,
  introLogoHeading,
  introLogoLink,
  logobarOptions,
  logoSuiteOptions,
  hideLogoHeading,
  hasMarqueeTopBorder,
}: RenderIntroLogosArgs): ReactElement {
  // Tiles is the non-motion default when the author has not picked a type.
  if (introLogoType !== 'Marquee') {
    return (
      <Box marginBlockStart={48} data-testid="intro-tiles">
        {introLogoHeading ? (
          hideLogoHeading ? (
            // Keep the heading announceable but don't reserve any visual space.
            <Text as="p" size="200" variant="muted" align="center" className="sr-only">
              {introLogoHeading}
            </Text>
          ) : (
            <Box paddingBlockEnd={{narrow: 'spacious', regular: 64}} paddingBlockStart="spacious">
              <Text as="p" size="200" variant="muted" align="center">
                {introLogoHeading}
              </Text>
            </Box>
          )
        ) : null}
        <Tiles variant="gridlines" layout="compact">
          {introLogos.map(logo => (
            <Tiles.Item key={logo} name={logo} data-testid={`intro-tile-${logo}`}>
              <Logo name={logo} />
            </Tiles.Item>
          ))}
        </Tiles>
      </Box>
    )
  }

  const takeoverButton =
    logobarOptions?.takeoverButton ??
    (introLogoLink?.fields.href ? {label: introLogoLink.fields.text, href: introLogoLink.fields.href} : undefined)

  return (
    <LogoSuite
      align="center"
      variant={logoSuiteOptions?.variant}
      className={hasMarqueeTopBorder ? styles.marqueeTopBorder : undefined}
    >
      {introLogoHeading ? (
        <LogoSuite.Heading visuallyHidden={hideLogoHeading}>{introLogoHeading}</LogoSuite.Heading>
      ) : null}
      <LogoSuite.Logobar
        variant={logobarOptions?.variant}
        marquee
        marqueeSpeed={logobarOptions?.marqueeSpeed}
        takeoverButton={takeoverButton}
      >
        {introLogos.map(logo => (
          <Logo key={logo} name={logo} />
        ))}
      </LogoSuite.Logobar>
    </LogoSuite>
  )
}

type IntroContextContentProps = Pick<
  IntroSectionProps,
  | 'introContext'
  | 'introContextHeading'
  | 'introContextLink'
  | 'introContextBody'
  | 'introItems'
  | 'introIconCards'
  | 'introPillars'
  | 'introImageCards'
  | 'introStats'
>

function renderIntroContextContent({
  introContext,
  introContextHeading,
  introContextLink,
  introContextBody,
  introItems,
  introIconCards,
  introPillars,
  introImageCards,
  introStats,
}: IntroContextContentProps): ReactNode {
  if (!introContext) return null

  switch (introContext) {
    case 'Simple Intro':
      return (
        <IntroSimpleIntro
          introContextHeading={introContextHeading}
          introContextBody={introContextBody}
          introContextLink={introContextLink}
          introItems={introItems}
        />
      )
    case 'Pillars':
      return <IntroPillars introPillars={introPillars} />
    case 'Icon Cards':
      return <IntroIconCards introIconCards={introIconCards} />
    case 'Image Cards':
      return <IntroImageCards introImageCards={introImageCards} />
    case 'Stats':
      return <IntroStats introStats={introStats} />
    default:
      return null
  }
}
